/**
 * DCTA-162 — the Read aloud choice (spoken.md §1.2, F29), kept while backend B43 is not built.
 *
 * The bank keeps `read_aloud` as a switch today (true/false; `PATCH /v1/customer/preferences` takes a boolean). B43
 * makes it "when_i_speak" | "always" | "never", reading a stored false as When I speak (the switch was never operable)
 * and true as Always. Until then the app writes the switch the bank has — Always = true, anything else false — and keeps
 * the difference between When I speak and Never as a note on this device, per customer (a convenience: the bank's value
 * wins whenever the two disagree). Once the bank answers with the three-way value, that is what is read and written and
 * the note is not consulted: nothing here changes when B43 lands.
 */
import type { Preferences } from "../api/types.js";
import type { ReadAloudMode } from "./read-aloud.js";

const MODES: readonly ReadAloudMode[] = ["when_i_speak", "always", "never"];
const isMode = (v: unknown): v is ReadAloudMode => typeof v === "string" && (MODES as readonly string[]).includes(v);

/** The choice from what the bank keeps (`stored`) and this device's note. */
export function modeOf(stored: unknown, note: string | null): ReadAloudMode {
  if (isMode(stored)) return stored;
  const noted = isMode(note) ? note : undefined;
  if (stored === true) return "always";
  if (stored === false) return noted === "never" ? "never" : "when_i_speak";
  return noted ?? "when_i_speak";
}

/** What goes to the bank: the three-way value once it speaks it (`modes`), else its switch. */
export function patchFor(mode: ReadAloudMode, modes: boolean): { readonly read_aloud: boolean | ReadAloudMode } {
  return { read_aloud: modes ? mode : mode === "always" };
}

export class ReadAloudSetting {
  readonly #key: string;
  readonly #storage: () => Storage | undefined;
  #mode: ReadAloudMode = "when_i_speak";
  /** The bank answered with the three-way value (B43 is live). */
  #modes = false;

  constructor(customerId: string, storage: () => Storage | undefined) {
    this.#key = `dcta.read-aloud.${customerId}`;
    this.#storage = storage;
  }

  get mode(): ReadAloudMode {
    return this.#mode;
  }

  /** The session's preferences (`GET /v1/session`, the login's answer). */
  load(prefs: Partial<Preferences> | undefined): void {
    const stored = prefs?.read_aloud;
    this.#modes = isMode(stored);
    this.#mode = modeOf(stored, this.#note());
  }

  /** The customer's choice: kept at once, written to the bank; `false` (and the old choice back) when the bank did not keep it. */
  async choose(mode: ReadAloudMode, write: (patch: { readonly read_aloud: boolean | ReadAloudMode }) => Promise<unknown>): Promise<boolean> {
    const before = this.#mode;
    this.#mode = mode;
    this.#remember(mode);
    try {
      await write(patchFor(mode, this.#modes));
      return true;
    } catch {
      // Put back only what this choice changed: a newer choice made meanwhile stays.
      if (this.#mode === mode) {
        this.#mode = before;
        this.#remember(before);
      }
      return false;
    }
  }

  #note(): string | null {
    try {
      return this.#storage()?.getItem(this.#key) ?? null;
    } catch {
      return null;
    }
  }

  #remember(mode: ReadAloudMode): void {
    try {
      this.#storage()?.setItem(this.#key, mode);
    } catch {
      /* a private window: the choice holds for this session */
    }
  }
}
